// src/pages/brands/BrandDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Divider,
  Paper,
  Avatar,
  Chip,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Link,
  useTheme,
  alpha,
  Breadcrumbs,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  BarChart as AnalyticsIcon,
  Campaign as CampaignIcon,
  ContentPaste as ContentIcon,
  Style as StyleIcon,
  RecordVoiceOver as VoiceIcon,
  Public as PublicIcon,
  AutoAwesome as MagicIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  NavigateNext as NavigateNextIcon,
  ColorLens as ColorIcon,
  TextFields as FontIcon,
  Language as LanguageIcon,
  CompareArrows as CompareArrowsIcon,
  Psychology as SOSTACIcon,
} from '@mui/icons-material';

// Import custom components
import { Card } from '../../design-system';
import { MagicLoader } from '../../design-system';
import { Button } from '../../design-system/components/core';
import { EmptyState } from '../../design-system/components/core';
import BrandAssetManager from './BrandAssetManager';
import SocialAccountsManager from './SocialAccountsManager';
import CompetitorsManager from './CompetitorsManager';
import SecureAvatar from '../../components/ui/SecureAvatar';

// Import hooks and stores
import useBrandStore from '../../stores/brandStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';

// Tab Panel component
// Import enhanced TabPanel from ui components
import { TabPanel, ContentPanel } from '../../components/ui';

// Status chip component
const StatusChip = ({ status }) => {
  // Define colors and labels based on status
  const statusConfig = {
    active: { color: 'success', label: 'Active' },
    inactive: { color: 'error', label: 'Inactive' },
    draft: { color: 'default', label: 'Draft' },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <Chip 
      label={config.label} 
      color={config.color} 
      size="small" 
      sx={{ fontWeight: 500 }}
    />
  );
};

// Brand detail page component
const BrandDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    currentBrand, 
    canViewCredentials,
    isLoading, 
    error, 
    fetchBrand, 
    fetchBrandAssets,
    fetchBrandColors,
    fetchBrandFonts,
    deleteBrand,
    fetchBrandCampaigns,
    fetchBrandContent, 
    updateBrandSocialAccounts,
    updateBrandCompetitors,
    uploadAsset,
    deleteAsset
  } = useBrandStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [assets, setAssets] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [content, setContent] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  
  // Check permissions
  const canUpdate = hasPermission('brands', 'update');
  const canDelete = hasPermission('brands', 'delete');
  const canCreateCampaign = hasPermission('campaigns', 'create');
  const canCreateContent = hasPermission('content', 'create');
  
  // Fetch brand on component mount
  useEffect(() => {
    fetchBrand(id);
  }, [fetchBrand, id]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Load data for the selected tab if it hasn't been loaded yet
    if (newValue === 1 && assets.length === 0) {
      loadAssets();
    } else if (newValue === 2 && campaigns.length === 0) {
      loadCampaigns();
    } else if (newValue === 3 && content.length === 0) {
      loadContent();
    }
  };
  
  // Load assets data
  const loadAssets = async () => {
    setIsLoadingAssets(true);
    try {
      const assetsData = await fetchBrandAssets(id);
      setAssets(assetsData || []);
    } catch (error) {
      showError('Failed to load assets: ' + error.message);
    }
    setIsLoadingAssets(false);
  };
  
  // Load campaigns data
  const loadCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const campaignsData = await fetchBrandCampaigns(id);
      setCampaigns(campaignsData || []);
    } catch (error) {
      showError('Failed to load campaigns: ' + error.message);
    }
    setIsLoadingCampaigns(false);
  };
  
  // Load content data
  const loadContent = async () => {
    setIsLoadingContent(true);
    try {
      const contentData = await fetchBrandContent(id);
      setContent(contentData || []);
    } catch (error) {
      showError('Failed to load content: ' + error.message);
    }
    setIsLoadingContent(false);
  };
  
  // Handle menu open
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Handle brand deletion
  const handleDeleteBrand = async () => {
    if (window.confirm(`Are you sure you want to delete ${currentBrand.name}?`)) {
      setIsDeleting(true);
      try {
        await deleteBrand(id);
        showSuccess(`Brand "${currentBrand.name}" deleted successfully`);
        navigate('/brands');
      } catch (error) {
        showError(`Failed to delete brand: ${error.message}`);
        setIsDeleting(false);
      }
    }
    
    handleMenuClose();
  };
  
  // Get client details
  const getClientName = () => {
    return currentBrand?.clientId?.name || 'N/A';
  };
  
  // Format industry
  const formatIndustry = () => {
    if (!currentBrand?.industry) return 'Not specified';
    
    let result = currentBrand.industry.primary || '';
    if (currentBrand.industry.subCategory) {
      result += ` - ${currentBrand.industry.subCategory}`;
    }
    return result;
  };
  
  // Get primary color
  const getPrimaryColor = () => {
    if (!currentBrand?.colors || currentBrand.colors.length === 0) return theme.palette.primary.main;
    
    const primaryColor = currentBrand.colors.find(c => c.isPrimary);
    return primaryColor ? primaryColor.hex : currentBrand.colors[0].hex;
  };
  
  // Define tabs
  const tabs = [
    { label: 'Identity', icon: <StyleIcon /> },
    { label: 'Assets', icon: <BusinessIcon /> },
    { label: 'Campaigns', icon: <CampaignIcon /> },
    { label: 'Content', icon: <ContentIcon /> },
    { label: 'Social Accounts', icon: <PublicIcon /> },
    { label: 'Competitors', icon: <CompareArrowsIcon /> },
    { label: 'SOSTAC Analysis', icon: <SOSTACIcon /> }
  ];
  
  // Loading state
  if (isLoading && !currentBrand) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message="Summoning brand details..." />
      </Box>
    );
  }
  
  // Error state
  if (error && !currentBrand) {
    return (
      <Box sx={{ py: 3 }}>
        <Card
          elevation={0}
          sx={{ 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Error Loading Brand
          </Typography>
          <Typography variant="body1" paragraph>
            {error}
          </Typography>
          <Button
            variant="outlined"
            
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/brands')}
          >
            Back to Brands
          </Button>
        </Card>
      </Box>
    );
  }
  
  // If brand is not loaded yet
  if (!currentBrand) {
    return null;
  }
  
  // Get primary color for styling
  const primaryColor = getPrimaryColor();
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link 
          underline="hover" 
          color="inherit" 
          component={RouterLink} 
          to="/brands"
        >
          Brands
        </Link>
        <Typography color="text.primary">{currentBrand.name}</Typography>
      </Breadcrumbs>
      
      {/* Header with brand info and actions */}
      <Card 
        magical={true} 
        level={2}
        sx={{ 
          mb: 4,
          borderTop: `4px solid ${primaryColor}`,
        }}
      >
        <CardContent>
          <Grid container spacing={3}>
            {/* Logo and basic info */}
            <Grid item xs={12} sm={8}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SecureAvatar
                  src={currentBrand.logo?.primary}
                  alt={currentBrand.name}
                  version="thumbnail"
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: alpha(primaryColor, 0.1),
                    color: primaryColor,
                    mr: 3,
                    border: `1px solid ${alpha(primaryColor, 0.2)}`,
                  }}
                >
                  {currentBrand.name.charAt(0)}
                </SecureAvatar>
                
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mr: 2 }}>
                      {currentBrand.name}
                    </Typography>
                    <StatusChip status={currentBrand.status} />
                    
                    {/* Primary brand indicator */}
                    {currentBrand.isPrimary && (
                      <Chip 
                        label="Primary Brand" 
                         
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                    )}
                    
                    {/* Magical indicator if applicable */}
                    {currentBrand.magicalElements?.enchantmentLevel && (
                      <Tooltip title={`Magical level: ${currentBrand.magicalElements.enchantmentLevel}`}>
                        <MagicIcon 
                          sx={{ 
                            ml: 1, 
                            color: theme.palette.accent?.main || '#FFD700',
                            animation: 'pulse 2s infinite ease-in-out',
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 0.7, transform: 'scale(1)' },
                              '50%': { opacity: 1, transform: 'scale(1.2)' },
                            },
                          }} 
                        />
                      </Tooltip>
                    )}
                  </Box>
                  
                  <Typography variant="subtitle1" color="textSecondary">
                    Client: {getClientName()}
                  </Typography>
                  
                  <Typography variant="body1" color="textSecondary">
                    {formatIndustry()}
                  </Typography>

                     {/* Service Configuration */}
                     {currentBrand.serviceConfig && 
                              <>
                                      <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                                        Platforms
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                        {currentBrand.serviceConfig.platforms && Object.entries(currentBrand.serviceConfig.platforms)
                                          .filter(([_, enabled]) => enabled)
                                          .map(([platform]) => (
                                            <Chip 
                                              key={platform} 
                                              label={platform?.charAt(0)?.toUpperCase() + platform?.slice(1)} 
                                              size="small"
                                              variant="outlined"
                                            />
                                          ))}
                                      </Box>
                                      
                                      <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                                        Services
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                        {currentBrand.serviceConfig.services && Object.entries(currentBrand.serviceConfig.services)
                                          .filter(([_, enabled]) => enabled)
                                          .map(([service]) => (
                                            <Chip 
                                              key={service} 
                                              label={service
                                                .replace(/([A-Z])/g, ' $1')
                                                .replace(/^./, str => str.toUpperCase())} 
                                              size="small"
                                              color="secondary"
                                              variant="outlined"
                                            />
                                          ))}
                                      </Box>
                                      
                                      <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                          <Typography variant="body2" color="textSecondary">
                                            Posting Frequency:
                                          </Typography>
                                          <Typography variant="body1" fontWeight={500}>
                                            {currentBrand.serviceConfig.postingFrequency === 'custom' 
                                              ? `Every ${currentBrand.serviceConfig.customFrequency} days` 
                                              : currentBrand.serviceConfig.postingFrequency?.replace('_', ' ')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography variant="body2" color="textSecondary">
                                            Reporting Frequency:
                                          </Typography>
                                          <Typography variant="body1" fontWeight={500}>
                                            {currentBrand.serviceConfig.reportingFrequency?.replace('_', ' ')}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </>
                                  }
                </Box>
              </Box>
            </Grid>
            
            {/* Action buttons */}
            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, alignItems: 'flex-start', gap: 1 }}>
              {canUpdate && (
                <Button
                  variant="outlined"
                  
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/brands/${id}/edit`)}
                >
                  Edit
                </Button>
              )}
              
              <Button
                variant="outlined"
                
                startIcon={<AnalyticsIcon />}
                onClick={() => navigate(`/analytics/brands/${id}`)}
              >
                Analytics
              </Button>
              
              <IconButton
                onClick={handleMenuOpen}
                sx={{ 
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
              >
                <MoreVertIcon />
              </IconButton>
              
              {/* Actions Menu */}
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                {canCreateCampaign && (
                  <MenuItem onClick={() => {
                    handleMenuClose();
                    navigate(`/campaigns/create?brandId=${id}`);
                  }}>
                    <ListItemIcon>
                      <CampaignIcon fontSize="small" />
                    </ListItemIcon>
                    New Campaign
                  </MenuItem>
                )}
                
                {canCreateContent && (
                  <MenuItem onClick={() => {
                    handleMenuClose();
                    navigate(`/content/create?brandId=${id}`);
                  }}>
                    <ListItemIcon>
                      <ContentIcon fontSize="small" />
                    </ListItemIcon>
                    New Content
                  </MenuItem>
                )}
                
                <MenuItem onClick={() => {
                  handleMenuClose();
                  navigate(`/sostac/new?brandId=${id}`);
                }}>
                  <ListItemIcon>
                    <SOSTACIcon fontSize="small" />
                  </ListItemIcon>
                  New SOSTAC Analysis
                </MenuItem>
                
                {currentBrand.guidelines && (
                  <MenuItem onClick={() => {
                    handleMenuClose();
                    window.open(currentBrand.guidelines.url, '_blank');
                  }}>
                    <ListItemIcon>
                      <DownloadIcon fontSize="small" />
                    </ListItemIcon>
                    Download Guidelines
                  </MenuItem>
                )}
                
                <Divider />
                
                {canDelete && (
                  <MenuItem 
                    onClick={handleDeleteBrand}
                    disabled={isDeleting}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Typography color="error">Delete Brand</Typography>
                  </MenuItem>
                )}
              </Menu>
            </Grid>
          </Grid>
          
          {/* Brand description */}
          {currentBrand.description && (
            <Typography variant="body1" paragraph sx={{ mt: 3 }}>
              {currentBrand.description}
            </Typography>
          )}
          
          {/* Color palette preview */}
          {currentBrand.colors && currentBrand.colors.length > 0 && (
            <Box sx={{ display: 'flex', mt: 2, mb: 1, gap: 1 }}>
              {currentBrand.colors.map((color, index) => (
                <Tooltip key={index} title={`${color.name}: ${color.hex}`}>
                  <Box 
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: color.hex,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Tabs for brand details */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="brand details tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.secondary.main,
              height: 3,
              borderRadius: '2px 2px 0 0',
              boxShadow: `0 0 8px ${theme.palette.secondary.main}`
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              id={`brand-tab-${index}`}
              aria-controls={`brand-tabpanel-${index}`}
              sx={{
                minHeight: 48,
                padding: '12px 16px',
                color: theme.palette.mode === 'dark'
                  ? theme.palette.common.white
                  : theme.palette.common.black,
                '&.Mui-selected': {
                  color: theme.palette.secondary.main,
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                }
              }}
            />
          ))}
        </Tabs>
      </Box>
      
      {/* Identity Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={4}>
          {/* Logos Section */}
          <Grid item xs={12}>
            <Card title="Brand Logos" magical={false} headerProps={{ avatar: <StyleIcon /> }}>
              <CardContent>
                <Grid container spacing={3}>
                  {/* Primary Logo */}
                  {currentBrand.logo?.primary && (
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Primary Logo
                        </Typography>
                        <SecureAvatar
                          src={currentBrand.logo.primary}
                          alt={`${currentBrand.name} Primary Logo`}
                          version="preview"
                          sx={{
                            width: 120,
                            height: 120,
                            mx: 'auto',
                            mb: 1,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }}
                        />
                        {currentBrand.logo.primary.originalname && (
                          <Typography variant="caption" color="textSecondary">
                            {currentBrand.logo.primary.originalname}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Secondary Logo */}
                  {currentBrand.logo?.secondary && (
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Secondary Logo
                        </Typography>
                        <SecureAvatar
                          src={currentBrand.logo.secondary}
                          alt={`${currentBrand.name} Secondary Logo`}
                          version="preview"
                          sx={{
                            width: 120,
                            height: 120,
                            mx: 'auto',
                            mb: 1,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }}
                        />
                        {currentBrand.logo.secondary.originalname && (
                          <Typography variant="caption" color="textSecondary">
                            {currentBrand.logo.secondary.originalname}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Favicon */}
                  {currentBrand.logo?.favicon && (
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Favicon
                        </Typography>
                        <SecureAvatar
                          src={currentBrand.logo.favicon}
                          alt={`${currentBrand.name} Favicon`}
                          version="thumbnail"
                          sx={{
                            width: 64,
                            height: 64,
                            mx: 'auto',
                            mb: 1,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }}
                        />
                        {currentBrand.logo.favicon.originalname && (
                          <Typography variant="caption" color="textSecondary">
                            {currentBrand.logo.favicon.originalname}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  
                  {/* No logos message */}
                  {(!currentBrand.logo?.primary && !currentBrand.logo?.secondary && !currentBrand.logo?.favicon) && (
                    <Grid item xs={12}>
                      <Typography color="textSecondary" align="center">
                        No logos uploaded yet.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Colors Section */}
          <Grid item xs={12} md={6}>
            <Card title="Brand Colors" magical={false} headerProps={{ avatar: <ColorIcon /> }}>
              <CardContent>
                {currentBrand.colors && currentBrand.colors.length > 0 ? (
                  <Grid container spacing={2}>
                    {currentBrand.colors.map((color, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '8px',
                              backgroundColor: color.hex,
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              mr: 2,
                            }}
                          />
                          <Box>
                            <Typography variant="subtitle2">
                              {color.name}
                              {color.isPrimary && (
                                <Chip 
                                  label="Primary" 
                                  size="small" 
                                   
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {color.hex.toUpperCase()}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography color="textSecondary">
                    No color palette defined.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Typography Section */}
          <Grid item xs={12} md={6}>
            <Card title="Typography" magical={false} headerProps={{ avatar: <FontIcon /> }}>
              <CardContent>
                {currentBrand.fonts && currentBrand.fonts.length > 0 ? (
                  <Grid container spacing={2}>
                    {currentBrand.fonts.map((font, index) => (
                      <Grid item xs={12} key={index}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">
                            {font.name}
                            <Chip 
                              label={font.category?.charAt(0)?.toUpperCase() + font.category?.slice(1)} 
                              size="small" 
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                          {font.url && (
                            <Typography variant="body2" color="textSecondary" noWrap>
                              {font.url}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography color="textSecondary">
                    No typography defined.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Brand Voice & Tone */}
          <Grid item xs={12} md={6}>
            <Card title="Voice & Tone" magical={false} headerProps={{ avatar: <VoiceIcon /> }}>
              <CardContent>
                {currentBrand.voice ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Tone: {currentBrand.voice.tone?.charAt(0)?.toUpperCase() + currentBrand.voice.tone?.slice(1) || 'Not specified'}
                    </Typography>
                    
                    {currentBrand.voice.personality && currentBrand.voice.personality.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Personality Traits:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentBrand.voice.personality.map((trait, index) => (
                            <Chip key={index} label={trait} size="small" />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {currentBrand.voice.values && currentBrand.voice.values.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Values:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentBrand.voice.values.map((value, index) => (
                            <Chip key={index} label={value} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {currentBrand.voice.keyMessages && currentBrand.voice.keyMessages.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Key Messages:
                        </Typography>
                        <List dense>
                          {currentBrand.voice.keyMessages.map((message, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={message} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography color="textSecondary">
                    No voice and tone defined.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Regional Settings */}
          <Grid item xs={12} md={6}>
            <Card title="Regional Settings" magical={false} headerProps={{ avatar: <LanguageIcon /> }}>
              <CardContent>
                {currentBrand.regionalSettings ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Primary Language: 
                      {currentBrand.regionalSettings.primaryLanguage === 'bilingual' ? ' Bilingual (English & Arabic)' : 
                      currentBrand.regionalSettings.primaryLanguage === 'en' ? ' English' : ' Arabic'}
                    </Typography>
                    
                    {currentBrand.regionalSettings.primaryLanguage !== 'en' && (
                      <Typography variant="subtitle2" gutterBottom>
                        Dialect: {currentBrand.regionalSettings.dialect?.charAt(0)?.toUpperCase() + currentBrand.regionalSettings.dialect?.slice(1) || 'Not specified'}
                      </Typography>
                    )}
                    
                    {currentBrand.regionalSettings.targetRegions && currentBrand.regionalSettings.targetRegions.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Target Regions:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentBrand.regionalSettings.targetRegions.map((region, index) => (
                            <Chip key={index} label={region} size="small" icon={<PublicIcon />} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {currentBrand.regionalSettings.culturalConsiderations && currentBrand.regionalSettings.culturalConsiderations.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Cultural Considerations:
                        </Typography>
                        <List dense>
                          {currentBrand.regionalSettings.culturalConsiderations.map((consideration, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={consideration} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography color="textSecondary">
                    No regional settings defined.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Website Information */}
          {currentBrand.website && (
            <Grid item xs={12} md={6}>
              <Card title="Website" magical={false} headerProps={{ avatar: <LanguageIcon /> }}>
                <CardContent>
                  {currentBrand.website.url ? (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        URL:
                      </Typography>
                      <Link 
                        href={currentBrand.website.url.startsWith('http') ? currentBrand.website.url : `https://${currentBrand.website.url}`} 
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <LanguageIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {currentBrand.website.url}
                        <OpenInNewIcon fontSize="small" sx={{ ml: 0.5 }} />
                      </Link>
                      
                      <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                        <Chip 
                          label="Blog" 
                          color={currentBrand.website.hasBlog ? "primary" : "default"}
                          variant={currentBrand.website.hasBlog ? "filled" : "outlined"}
                        />
                        <Chip 
                          label="E-commerce" 
                          color={currentBrand.website.hasEcommerce ? "primary" : "default"}
                          variant={currentBrand.website.hasEcommerce ? "filled" : "outlined"}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Typography color="textSecondary">
                      No website information defined.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Magical Elements */}
          {currentBrand.magicalElements && (
            <Grid item xs={12}>
              <Card 
                title="Magical Elements" 
                magical={true} 
                level={2}
                headerProps={{ 
                  avatar: <MagicIcon sx={{ color: theme.palette.accent?.main || '#FFD700' }} /> 
                }}
              >
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Enchantment Level:
                      </Typography>
                      <Chip 
                        label={currentBrand.magicalElements.enchantmentLevel?.charAt(0)?.toUpperCase() + currentBrand.magicalElements.enchantmentLevel?.slice(1)} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.accent?.main || '#FFD700', 0.1),
                          color: theme.palette.accent?.main || '#FFD700',
                          fontWeight: 500,
                        }}
                      />
                    </Grid>
                    
                    {currentBrand.magicalElements.specialFeatures && currentBrand.magicalElements.specialFeatures.length > 0 && (
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle2" gutterBottom>
                          Special Features:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentBrand.magicalElements.specialFeatures.map((feature, index) => (
                            <Chip 
                              key={index} 
                              label={feature} 
                              icon={<MagicIcon fontSize="small" />}
                              variant="outlined"
                              sx={{ 
                                borderColor: alpha(theme.palette.accent?.main || '#FFD700', 0.3),
                                color: theme.palette.text.primary,
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                    
                    {currentBrand.magicalElements.transformationNarrative && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Transformation Narrative:
                        </Typography>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            borderColor: alpha(theme.palette.accent?.main || '#FFD700', 0.3),
                            bgcolor: alpha(theme.palette.accent?.main || '#FFD700', 0.05),
                          }}
                        >
                          <Typography variant="body2">
                            {currentBrand.magicalElements.transformationNarrative}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>
      
      {/* Assets Tab */}
      <TabPanel value={tabValue} index={1}>
        <BrandAssetManager 
          brandId={id}
          assets={assets}
          onUpload={async (brandId, file, metadata) => {
            try {
              await uploadAsset(brandId, file, metadata);
              await loadAssets();
              showSuccess('Asset uploaded successfully');
            } catch (error) {
              showError('Failed to upload asset: ' + error.message);
            }
          }}
          onDelete={async (brandId, assetId) => {
            try {
              await deleteAsset(brandId, assetId);
              await loadAssets();
              showSuccess('Asset deleted successfully');
            } catch (error) {
              showError('Failed to delete asset: ' + error.message);
            }
          }}
          isLoading={isLoadingAssets}
          canUpload={canUpdate}
        />
      </TabPanel>
      
      {/* Campaigns Tab */}
      <TabPanel value={tabValue} index={2}>
        {isLoadingCampaigns ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <MagicLoader size="medium" message="Loading campaigns..." />
          </Box>
        ) : campaigns.length === 0 ? (
          <EmptyState
            title="No Campaigns Yet"
            description="Create marketing campaigns for this brand."
            icon={<CampaignIcon sx={{ fontSize: 64 }} />}
            action={
              canCreateCampaign ? (
                <Button
                  variant="contained"
                  
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/campaigns/create?brandId=${id}`)}
                  magical={true}
                >
                  Create Campaign
                </Button>
              ) : null
            }
          />
        ) : (
          <Grid container spacing={3}>
            {campaigns.map(campaign => (
              <Grid item xs={12} sm={6} md={4} key={campaign._id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{campaign.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {campaign.description}
                    </Typography>
                    {/* Additional campaign details would go here */}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>
      
      {/* Content Tab */}
      <TabPanel value={tabValue} index={3}>
        {isLoadingContent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <MagicLoader size="medium" message="Loading content..." />
          </Box>
        ) : content.length === 0 ? (
          <EmptyState
            title="No Content Yet"
            description="Create social media posts and content for this brand."
            icon={<ContentIcon sx={{ fontSize: 64 }} />}
            action={
              canCreateContent ? (
                <Button
                  variant="contained"
                  
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/content/create?brandId=${id}`)}
                  magical={true}
                >
                  Create Content
                </Button>
              ) : null
            }
          />
        ) : (
          <Grid container spacing={3}>
            {content.map(contentItem => (
              <Grid item xs={12} sm={6} md={4} key={contentItem._id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{contentItem.title}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {contentItem.description}
                    </Typography>
                    {/* Additional content details would go here */}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>
      
      {/* Social Accounts Tab */}
      <TabPanel value={tabValue} index={4}>
        <SocialAccountsManager
          accounts={currentBrand?.socialAccounts || []}
          onAdd={async (accountData) => {
            try {
              // Clone the current social accounts and add the new one
              const updatedAccounts = [...(currentBrand?.socialAccounts || []), accountData];
              
              // Call the API to update
              await updateBrandSocialAccounts(id, updatedAccounts);
              
              // Refresh brand details
              await fetchBrand(id);
              showSuccess('Social account added successfully');
            } catch (error) {
              showError('Failed to add social account: ' + error.message);
            }
          }}
          onUpdate={async (accountData) => {
            try {
              // Clone and update the accounts array
              const updatedAccounts = (currentBrand?.socialAccounts || []).map(account => 
                account._id === accountData._id ? accountData : account
              );
              
              // Call the API to update
              await updateBrandSocialAccounts(id, updatedAccounts);
              
              // Refresh brand details
              await fetchBrand(id);
              showSuccess('Social account updated successfully');
            } catch (error) {
              showError('Failed to update social account: ' + error.message);
            }
          }}
          onDelete={async (accountId) => {
            try {
              // Filter out the deleted account
              const updatedAccounts = (currentBrand?.socialAccounts || []).filter(
                account => account._id !== accountId
              );
              
              // Call the API to update
              await updateBrandSocialAccounts(id, updatedAccounts);
              
              // Refresh brand details
              await fetchBrand(id);
              showSuccess('Social account deleted successfully');
            } catch (error) {
              showError('Failed to delete social account: ' + error.message);
            }
          }}
          canEdit={canUpdate}
          canViewCredentials={canViewCredentials}
        />
      </TabPanel>
      
      {/* Competitors Tab */}
      <TabPanel value={tabValue} index={5}>
        <CompetitorsManager
          competitors={currentBrand?.competitors || []}
          onAdd={async (competitorData) => {
            try {
              // Clone the current competitors and add the new one
              const updatedCompetitors = [...(currentBrand?.competitors || []), competitorData];
              
              // Call the API to update
              await updateBrandCompetitors(id, updatedCompetitors);
              
              // Refresh brand details
              await fetchBrand(id);
              showSuccess('Competitor added successfully');
            } catch (error) {
              showError('Failed to add competitor: ' + error.message);
            }
          }}
          onUpdate={async (competitorData) => {
            try {
              // Clone and update the competitors array
              const updatedCompetitors = (currentBrand?.competitors || []).map(competitor => 
                competitor._id === competitorData._id ? competitorData : competitor
              );
              
              // Call the API to update
              await updateBrandCompetitors(id, updatedCompetitors);
              
              // Refresh brand details
              await fetchBrand(id);
              showSuccess('Competitor updated successfully');
            } catch (error) {
              showError('Failed to update competitor: ' + error.message);
            }
          }}
          onDelete={async (competitorId) => {
            try {
              // Filter out the deleted competitor
              const updatedCompetitors = (currentBrand?.competitors || []).filter(
                competitor => competitor._id !== competitorId
              );
              
              // Call the API to update
              await updateBrandCompetitors(id, updatedCompetitors);
              
              // Refresh brand details
              await fetchBrand(id);
              showSuccess('Competitor deleted successfully');
            } catch (error) {
              showError('Failed to delete competitor: ' + error.message);
            }
          }}
          canEdit={canUpdate}
        />
      </TabPanel>
      
      {/* SOSTAC Analysis Tab */}
      <TabPanel value={tabValue} index={6}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card 
              title="SOSTAC Marketing Analysis" 
              magical={false} 
              headerProps={{ avatar: <SOSTACIcon /> }}
            >
              <CardContent>
                <Box textAlign="center" py={4}>
                  <SOSTACIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Strategic Marketing Analysis Framework
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create comprehensive SOSTAC analysis for {currentBrand.name} to develop strategic marketing plans.
                  </Typography>
                  
                  <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate(`/sostac/new?brandId=${id}`)}
                      sx={{ minWidth: 180 }}
                    >
                      Create SOSTAC Analysis
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={<AnalyticsIcon />}
                      onClick={() => navigate(`/brands/${id}/sostac`)}
                      sx={{ minWidth: 180 }}
                    >
                      View Existing Analysis
                    </Button>
                  </Stack>
                  
                  <Box mt={4} p={3} bgcolor="background.paper" borderRadius={2} border="1px solid" borderColor="divider">
                    <Typography variant="subtitle2" gutterBottom>
                      SOSTAC Framework Components:
                    </Typography>
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="caption" fontWeight="bold">S - Situation</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Where are we now?
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="caption" fontWeight="bold">O - Objectives</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Where do we want to be?
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="caption" fontWeight="bold">S - Strategy</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          How do we get there?
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="caption" fontWeight="bold">T - Tactics</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          How exactly?
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="caption" fontWeight="bold">A - Action</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          What is our plan?
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="caption" fontWeight="bold">C - Control</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          How do we monitor?
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default BrandDetail;